import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Download, Search, Check, X, Eye, EyeOff, Edit2, RefreshCw, Shield, Mail, Save, Clock, Server, Database, AlertTriangle, CheckCircle, XCircle, FileText, Settings, Calendar, HardDrive } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

import { adminAPI, backupAPI } from '../../services/api';

export function DataBackupsPage() {
  const [backupStatus, setBackupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [backupEmail, setBackupEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    fetchBackupStatus();
  }, []);

  const fetchBackupStatus = async () => {
    try {
      const res = await backupAPI.getStatus();
      setBackupStatus(res.data);
      setBackupEmail(res.data.backup_email || 'it@singhaniainternational.com');
    } catch (error) {
      toast.error('Failed to load backup status');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!backupEmail || !backupEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSavingEmail(true);
    try {
      await backupAPI.updateSettings({ backup_email: backupEmail });
      toast.success('Backup delivery email updated');
      fetchBackupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await backupAPI.downloadWithAuth();
      
      // Create blob and download
      const blob = new Blob([res.data], { type: 'application/gzip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `erp_backup_${timestamp}.json.gz`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully');
      fetchBackupStatus(); // Refresh status
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-backups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" />
            Data Backups
          </h1>
          <p className="text-muted-foreground">
            Automated daily backups and manual backup downloads
          </p>
        </div>
        <Button 
          onClick={handleDownloadBackup} 
          disabled={downloading}
          className="btn-primary"
          data-testid="download-backup-btn"
        >
          {downloading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Generate & Download Backup Now
            </>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Next Scheduled Backup</p>
                <p className="text-lg font-semibold">
                  {backupStatus?.next_scheduled_backup 
                    ? new Date(backupStatus.next_scheduled_backup).toLocaleString()
                    : 'Not scheduled'}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Collections Backed Up</p>
                <p className="text-lg font-semibold">
                  {backupStatus?.collections_to_backup?.length || 0}
                </p>
              </div>
              <HardDrive className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Backup Frequency</p>
                <p className="text-lg font-semibold">Daily at 2:00 AM UTC</p>
              </div>
              <Clock className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backup Email Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Backup Delivery Email
          </CardTitle>
          <CardDescription>
            Configure where automated daily backups are sent
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-2">
              <Label htmlFor="backup-email">Recipient Email Address</Label>
              <Input
                id="backup-email"
                type="email"
                value={backupEmail}
                onChange={(e) => setBackupEmail(e.target.value)}
                placeholder="it@yourcompany.com"
                data-testid="backup-email-input"
              />
            </div>
            <Button 
              onClick={handleSaveEmail} 
              disabled={savingEmail}
              data-testid="save-backup-email-btn"
            >
              {savingEmail ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Save'
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            The nightly backup (.json.gz) will be automatically emailed to this address at 2:00 AM UTC.
          </p>
        </CardContent>
      </Card>

      {/* Collections List */}
      <Card>
        <CardHeader>
          <CardTitle>Backup Coverage</CardTitle>
          <CardDescription>
            Collections included in each backup
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {backupStatus?.collections_to_backup?.map((collection) => (
              <Badge key={collection} variant="outline" className="bg-muted/50">
                {collection}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Backup Files */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Backup Files</CardTitle>
          <CardDescription>
            Last 10 backup files stored on the server
          </CardDescription>
        </CardHeader>
        <CardContent>
          {backupStatus?.recent_backup_files?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backupStatus.recent_backup_files.map((file, idx) => (
                  <TableRow key={file.filename || `backup-${idx}`}>
                    <TableCell className="font-mono text-sm">{file.filename}</TableCell>
                    <TableCell>{file.size_kb} KB</TableCell>
                    <TableCell>{formatDate(file.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No backup files found</p>
              <p className="text-sm">Click "Generate & Download Backup Now" to create your first backup</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backup Info */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-primary mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Backup Information</p>
              <ul className="list-disc list-inside mt-2 text-muted-foreground space-y-1">
                <li>Automated backups are emailed to <strong>{backupEmail}</strong> daily at 2:00 AM UTC</li>
                <li>Backups are compressed JSON files containing all ERP data</li>
                <li>Manual backups can be downloaded at any time using the button above</li>
                <li>Backup files older than 7 days are automatically cleaned up</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


export default DataBackupsPage;
